import { createContext, type ReactNode, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';

import { getGroupTitle, getTaskTitle } from '@/Client/Lib/scopeNavigatorPartPresentersLib';
import { setGalleryPhotoDate } from '@/Client/Partials/FocusedPhotoGallery/galleryPhotoOperations';
import {
  getScanUrl,
  useGuardedNavigate,
  useNavigateToScanFocusPanelPage,
  useScanEditingContextSlice,
  useToast,
} from '@/Client/State';
import { useFeatureFlags } from '@kaiizen/kai-pro-api-react-state';
import {
  getScanPhotoTimeline,
  getScanPhotoTimelinePeriodStart,
  type ScanPhotoTimeline,
  type ScanPhotoTimelineRow,
  type ScanRootOptimized,
} from '@kaiizen/kai-scan-template-system';

import {
  PROGRESS_ALT_FULLSCREEN_PAGE,
  PROGRESS_ALT_PAGE,
  type ScanFocusPanelPage,
  type ScanFocusPanelScopeProps,
} from '../scanFocusPanelTypes';

import { readProgressAltCellAddress, writeProgressAltCellAddress } from './progressAltCellUrl';
import {
  findNeighborCells,
  findRevealTarget,
  getCursorAtCell,
  getCursorRequest,
  type ProgressAltCellCoordinates,
  type ProgressAltCursor,
  type ProgressAltCursorDirection,
  resolveCursor,
} from './progressAltCursorLib';
import {
  getProgressAltTileSize,
  PHOTO_VIEW_TIME_NAV_REACH,
  type ProgressAltTileSizeId,
} from './progressAltMetrics';
import { getColumnKey, type ProgressAltColumn } from './progressAltModel';
import {
  DEFAULT_PROGRESS_ALT_PERIOD_ID,
  getProgressAltPeriod,
  getProgressAltPeriodOptions,
  type ProgressAltPeriodId,
} from './progressAltPeriods';
import { getFirstPeriodStart, getRowNumber as getTimelineRowNumber } from './progressAltRowLabelLib';
import type { ProgressAltScrollPosition } from './progressAltScrollLib';
import {
  type ProgressAltRowNumber,
  readProgressAltCollapsedRows,
  readProgressAltGroupBy,
  writeProgressAltCollapsedRows,
  writeProgressAltGroupBy,
} from './progressAltViewUrl';

/**
 * Everything the Progress Alt panel is looking at, and every way it can be changed — taken straight
 * off what `useProgressAltValue` returns, so the shape is stated once rather than once as a type and
 * again as the object that satisfies it.
 */
type ProgressAltContextValue = ReturnType<typeof useProgressAltValue>;

const ProgressAltContext = createContext<ProgressAltContextValue | undefined>(undefined);

export const useProgressAlt = (): ProgressAltContextValue => {
  const context = useContext(ProgressAltContext);
  if (!context) throw new Error('useProgressAlt must be used within a ProgressAltProvider');
  return context;
};

const GROUP_COLUMN_SUBTITLE = 'Group';

/** Labels each timeline column, leaving the order it arrives in alone. */
const buildColumns = (
  scanRoot: ScanRootOptimized,
  timeline: ScanPhotoTimeline,
): ProgressAltColumn[] =>
  timeline.columns.map(column => {
    const group = scanRoot.groupsById[column.groupId];
    const task = column.taskId ? group?.tasksById[column.taskId] : undefined;
    const groupTitle = group ? getGroupTitle(group) : 'Untitled group';
    return {
      ...column,
      title: task ? getTaskTitle(task) : groupTitle,
      subtitle: task ? groupTitle : GROUP_COLUMN_SUBTITLE,
    };
  });

const toggleMember = <T,>(members: ReadonlySet<T>, member: T): ReadonlySet<T> => {
  const next = new Set(members);
  if (!next.delete(member)) next.add(member);
  return next;
};

/**
 * Everything either page reads, and every way it can be changed.
 *
 * One context rather than a chain of props, so the distance from the scan to whatever draws it is
 * always one hop. That matters most on the fullscreen page, whose details pane reads costs, options
 * and line items straight off the scan: an edit anywhere in the scan reaches it without a single
 * intermediate component having to know the pane exists.
 *
 * The scan half is derived from `useScanEditingContextSlice`, so it re-derives whenever the scan does.
 * The view half belongs to nothing in the scan, and splits again by whether it is worth addressing.
 * How the grid is cut and folded is read off the query string, so the URL in the bar reconstructs the
 * screen and can be sent to someone. How big the tiles are drawn and whether the date column is shut
 * are state of their own — a reader's own comfort, not a thing to link to. Either way it outlives a
 * trip through the fullscreen page, both pages being drawn by this one provider.
 */
const useProgressAltValue = ({
  activePanel,
  selectedGroupId,
  selectedTaskId,
  onSelectGroup,
  onSelectTask,
}: ProgressAltRouteSelection) => {
  const { scanRoot, scanId, scanIsEditable, scanDataMutators } = useScanEditingContextSlice([
    'scanRoot',
    'scanId',
    'scanIsEditable',
    'scanDataMutators',
  ]);
  const { featureFlags } = useFeatureFlags();
  const { showErrorToast } = useToast();
  const navigateToScanFocusPanelPage = useNavigateToScanFocusPanelPage();
  const navigate = useGuardedNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Which periods are on offer is settled by the URL the panel opened with, so it is read once.
  const [periodOptions] = useState(getProgressAltPeriodOptions);
  /**
   * How the grid is cut and folded is read off the query string rather than held in state, so the URL
   * in the bar reconstructs the screen: reload it and the rows come back the width they were, with the
   * same ones folded away. Both are written back with `replace` — folding a row is a change of view,
   * not a place the back button should have to walk out of.
   */
  const periodId = readProgressAltGroupBy(searchParams, DEFAULT_PROGRESS_ALT_PERIOD_ID);
  const collapsedRows = useMemo(
    () => readProgressAltCollapsedRows(searchParams, periodId),
    [periodId, searchParams],
  );
  const [tileSizeId, selectTileSize] = useState<ProgressAltTileSizeId>('small');
  const [dateColumnCollapsed, setDateColumnCollapsed] = useState(false);
  const [expandedColumnKeys, setExpandedColumnKeys] = useState<ReadonlySet<string>>(() => new Set());
  /**
   * Where the grid was scrolled to, held here because the grid itself cannot hold it: the fullscreen
   * page replaces it in the DOM, so its scroll container is destroyed and built again on every trip out
   * and back. Written on the way out and read on the way in by `useRememberedScrolling`; a ref rather
   * than state, nothing rendering differently for it.
   */
  const gridScroll = useRef<ProgressAltScrollPosition>({ scrollLeft: 0, scrollTop: 0 });

  const toggleDateColumnCollapsed = useCallback(() => setDateColumnCollapsed(previous => !previous), []);
  const toggleExpandedColumn = useCallback(
    (columnKey: string) => setExpandedColumnKeys(previous => toggleMember(previous, columnKey)),
    [],
  );

  const selectPeriod = useCallback(
    (nextPeriodId: ProgressAltPeriodId) => setSearchParams(writeProgressAltGroupBy(searchParams, nextPeriodId), { replace: true }),
    [searchParams, setSearchParams],
  );
  /**
   * Folds a row, or opens it back. Each grouping keeps its own folded rows, so this only ever writes
   * the one in force — which is what leaves the days folded where they were after a trip through the
   * weeks.
   */
  const toggleCollapsedRow = useCallback(
    (rowNumber: ProgressAltRowNumber) =>
      setSearchParams(
        writeProgressAltCollapsedRows(searchParams, periodId, toggleMember(collapsedRows, rowNumber)),
        { replace: true },
      ),
    [collapsedRows, periodId, searchParams, setSearchParams],
  );

  /**
   * The whole scan, always — a selected scope scrolls the grid to its column rather than narrowing the
   * timeline to it (KAIDEV-3881). The panel exists to read one group against the others over time, and
   * a timeline cut down to the selection would take away the very thing it is read for.
   */
  const timeline = useMemo(
    () =>
      scanRoot
        ? getScanPhotoTimeline(scanRoot, { period: getProgressAltPeriod(periodId) })
        : undefined,
    [scanRoot, periodId],
  );
  const columns = useMemo(
    () => scanRoot && timeline ? buildColumns(scanRoot, timeline) : [],
    [scanRoot, timeline],
  );
  const tileSize = useMemo(() => getProgressAltTileSize(tileSizeId), [tileSizeId]);

  /**
   * What a row is called — the ordinal its own label leads with, or the undated row's stand-in.
   *
   * Derived here because three surfaces have to agree on it: the date column that draws the number,
   * the grid that folds by it, and the query string that carries which rows are folded. Naming it in
   * one place is what lets a reader fold a row and see that same number appear in the URL.
   */
  const firstPeriodStart = useMemo(() => getFirstPeriodStart(timeline?.rows ?? []), [timeline]);
  const getRowNumber = useCallback(
    (row: Pick<ScanPhotoTimelineRow, 'periodStart'>) => getTimelineRowNumber(row, getProgressAltPeriod(periodId), firstPeriodStart),
    [firstPeriodStart, periodId],
  );
  /**
   * Which column the panel is pointed at, named the way the route names it.
   *
   * The scope the workspace has selected and the column the fullscreen page is showing are the same
   * fact, so they are the same two ids — read out of the group and task slots every other focused page
   * uses rather than out of parameters of Progress Alt's own.
   */
  const selectedColumnRef = useMemo(
    () => selectedGroupId ? { groupId: selectedGroupId, taskId: selectedTaskId } : undefined,
    [selectedGroupId, selectedTaskId],
  );
  // The rest of the address: which bucket of that column, and which of the cell's photos.
  const cellAddress = useMemo(() => readProgressAltCellAddress(searchParams), [searchParams]);

  /**
   * The cell the fullscreen page is showing, or nothing when the grid is what's showing.
   *
   * Which page the route names is what decides between them, so the panel's two modes are two
   * addresses rather than a piece of state. What the address opens on is still checked against the
   * timeline in hand: regroup by week or delete the photo and the same address resolves to a different
   * cell, or to none — which lands the reader on the grid rather than on an error.
   */
  const cursor = useMemo(
    () =>
      timeline && selectedColumnRef && activePanel === PROGRESS_ALT_FULLSCREEN_PAGE
        ? resolveCursor(timeline, { ...selectedColumnRef, ...cellAddress })
        : undefined,
    [activePanel, cellAddress, selectedColumnRef, timeline],
  );

  /**
   * Where the grid should be looking: the selected column, and the bucket the address names.
   *
   * This is the whole of what selecting a scope does to the grid — it scrolls, so the columns either
   * side stay in view to be read against. Resolved against the timeline in hand, so a column that has
   * since gone leaves the view where the reader left it rather than scrolling to nowhere.
   */
  const revealTarget = useMemo(
    () =>
      timeline && selectedColumnRef
        ? findRevealTarget(timeline, selectedColumnRef, cellAddress)
        : undefined,
    [cellAddress, selectedColumnRef, timeline],
  );

  /**
   * Every cell the fullscreen page can step to, nearest first, worked out once.
   *
   * Named buckets rather than bare directions, because the time navigator labels its buttons with where
   * they land. Derived here rather than in either consumer: the stage needs them for the strip and the
   * arrow keys, the details pane for its row moves, and computing them twice for one cursor would be two
   * walks of the same timeline.
   *
   * The scope axis reaches one step, its arrows being single moves; the time axis reaches further,
   * its buttons naming a stretch of the timeline rather than one step along it.
   */
  const photoNeighbors = useMemo(() => {
    const cellsToward = (direction: ProgressAltCursorDirection, reach: number) =>
      timeline && cursor ? findNeighborCells(timeline, cursor, direction, reach) : [];
    return {
      left: cellsToward('left', 1),
      right: cellsToward('right', 1),
      earlier: cellsToward('earlier', PHOTO_VIEW_TIME_NAV_REACH),
      later: cellsToward('later', PHOTO_VIEW_TIME_NAV_REACH),
    };
  }, [timeline, cursor]);

  const photoScope = useMemo(() => {
    if (!scanRoot || !cursor) return undefined;
    const column = columns[cursor.columnIndex];
    const group = column ? scanRoot.groupsById[column.groupId] : undefined;
    return { column, group, task: column?.taskId ? group?.tasksById[column.taskId] : undefined };
  }, [scanRoot, columns, cursor]);

  /**
   * Shows one cell on one of the panel's two pages: its scope in the route, its bucket and photo in the
   * query. The one way the panel moves — opening a photo, stepping to another cell, picking one out of
   * the strip and closing back to the grid are all this same write to the address.
   *
   * The cell is named by ids rather than by indices, because a re-scoped scan moves whatever sits at a
   * given index; `getCursorRequest` is what turns the one into the other.
   *
   * Opening is a push so the back button closes it; moving inside it is a replace, so a walk through a
   * scan's photos doesn't have to be walked back out again.
   */
  const showCell = useCallback((
    panel: ScanFocusPanelPage,
    next: ProgressAltCursor | undefined,
    { replace }: { replace: boolean },
  ) => {
    const request = timeline && next ? getCursorRequest(timeline, next) : undefined;
    if (!scanId || !request) return;
    const search = writeProgressAltCellAddress(searchParams, request).toString();
    navigate(
      {
        pathname: getScanUrl({ scanId, focusPanel: panel, groupId: request.groupId, taskId: request.taskId }),
        search: search ? `?${search}` : '',
      },
      { replace },
    );
  }, [navigate, scanId, searchParams, timeline]);

  const openPhoto = useCallback(
    (next: ProgressAltCursor) => showCell(PROGRESS_ALT_FULLSCREEN_PAGE, next, { replace: false }),
    [showCell],
  );

  const moveToCell = useCallback((coordinates: ProgressAltCellCoordinates) => {
    if (!timeline) return;
    // Every move lands on the cell's newest photo — the one the grid's tile was showing for it.
    showCell(PROGRESS_ALT_FULLSCREEN_PAGE, getCursorAtCell(timeline, coordinates), { replace: true });
  }, [showCell, timeline]);

  /** One step along either axis, onto the nearest cell that direction offers. */
  const movePhotoCursor = useCallback((direction: ProgressAltCursorDirection) => {
    const [target] = photoNeighbors[direction];
    if (target) moveToCell(target);
  }, [moveToCell, photoNeighbors]);

  const selectPhoto = useCallback((photoId: string) => {
    if (cursor) showCell(PROGRESS_ALT_FULLSCREEN_PAGE, { ...cursor, photoId }, { replace: true });
  }, [cursor, showCell]);

  /**
   * Back to the grid, still addressing the cell that was open — which is the cell the arriving grid
   * scrolls to, so the reader carries on from where they were reading rather than from the top left.
   */
  const closePhotoView = useCallback(
    () => showCell(PROGRESS_ALT_PAGE, cursor, { replace: false }),
    [cursor, showCell],
  );

  /**
   * The one way to change anything: the editor, with this scope already selected in the scan nav.
   *
   * One navigation through the nav lib rather than a selection followed by a panel switch — the scope
   * and the panel belong to the same address (`/scan/:scanId/group/:groupId/task/:taskId/edit`), so
   * setting them separately would put a half-moved state in the history on the way.
   */
  const editScope = useCallback(() => {
    const column = photoScope?.column;
    if (!column || !scanId) return;
    navigateToScanFocusPanelPage({ scanId, panel: 'edit', groupId: column.groupId, taskId: column.taskId });
  }, [navigateToScanFocusPanelPage, photoScope, scanId]);

  /**
   * Internal testing control: placing a photo on any day, to exercise the timeline without waiting
   * for real time to pass. Off for everyone the `testingTools` flag is not on for.
   */
  const canEditPhotoDate = scanIsEditable && featureFlags.testingTools;

  /**
   * Moves a photo to another day. Two writes in one breath, so they land in one render: the scan
   * takes the new date, and the address takes the bucket that date falls in. The bucket is worked
   * out from the date itself, because the timeline in hand still has the photo where it was.
   */
  const setPhotoDate = useCallback(async (photoId: string, addedAt: number) => {
    const saving = scanDataMutators.setScan(current => setGalleryPhotoDate(current, photoId, addedAt));
    setSearchParams(
      writeProgressAltCellAddress(searchParams, {
        at: getScanPhotoTimelinePeriodStart(addedAt, getProgressAltPeriod(periodId)),
        photoId,
      }),
      { replace: true },
    );
    try {
      await saving;
    } catch (error) {
      showErrorToast(error, 'update the date of this photo');
    }
  }, [periodId, scanDataMutators, searchParams, setSearchParams, showErrorToast]);

  /**
   * Unmemoised on purpose. Every field above is either state of this component, a `useCallback`, or a
   * `useMemo`, so a fresh wrapper changes nothing about what the consumers see — and the panel already
   * re-rendered its whole tree on any of these changing, back when they were props. A dependency array
   * here would be a third place to keep this list in step.
   */
  return {
    selectedGroupId,
    selectedTaskId,
    onSelectGroup,
    onSelectTask,
    scanRoot,
    scanIsEditable,
    timeline,
    columns,
    periodOptions,
    periodId,
    selectPeriod,
    tileSize,
    tileSizeId,
    selectTileSize,
    dateColumnCollapsed,
    toggleDateColumnCollapsed,
    expandedColumnKeys,
    toggleExpandedColumn,
    getRowNumber,
    collapsedRows,
    toggleCollapsedRow,
    cursor,
    revealTarget,
    gridScroll,
    photoNeighbors,
    photoScope,
    openPhoto,
    moveToCell,
    movePhotoCursor,
    selectPhoto,
    closePhotoView,
    editScope,
    canEditPhotoDate,
    setPhotoDate,
  };
};

/**
 * Where the route has the panel pointed, and how to move the selection. Passed in from the workspace,
 * which is what reads the route and checks both ids against the scan before they get here.
 */
type ProgressAltRouteSelection = Pick<
  ScanFocusPanelScopeProps,
  'activePanel' | 'selectedGroupId' | 'selectedTaskId' | 'onSelectGroup' | 'onSelectTask'
>;

export const ProgressAltProvider = ({ children, ...routeSelection }: ProgressAltRouteSelection & {
  children: ReactNode;
}) => (
  <ProgressAltContext.Provider value={useProgressAltValue(routeSelection)}>
    {children}
  </ProgressAltContext.Provider>
);
